import { Box, Flex, MenuContent, MenuItem, MenuPositioner, MenuRoot, MenuTrigger, Portal, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { CaretDownIcon, CheckIcon, GlobeSimpleIcon } from '@phosphor-icons/react';
import { Eyebrow } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW, INK_RULE } from '@/travo/theme/constants';
import { useNavItems } from './useNavItems';

interface LanguageMenuProps {
  /** Mobile menu: closes the dialog after a pick, and renders in place so the menu layers above it. */
  onNavigate?: () => void;
}

/** Outlined pill that opens the same page in another language. Crawlers get hreflang alternates from SEO. */
export const LanguageMenu = ({ onNavigate }: LanguageMenuProps) => {
  const copy = useCopy();
  const { locale, languages } = useNavItems();
  const current = languages.find((item) => item.locale === locale);
  const inDialog = onNavigate !== undefined;

  return (
    <MenuRoot positioning={{ placement: inDialog ? 'bottom-start' : 'bottom-end', gutter: 10 }}>
      <MenuTrigger asChild>
        <Box
          as="button"
          aria-label={`${copy.nav.language}: ${current?.name}`}
          display="inline-flex"
          alignItems="center"
          gap="6px"
          px="12px"
          py="6px"
          color="ink"
          fontSize="14px"
          fontWeight="500"
          border="2px solid"
          borderColor="ink"
          borderRadius="full"
          cursor="pointer"
          transition="transform 0.15s, box-shadow 0.15s, background 0.15s"
          _hover={{ bg: 'card', transform: 'translate(-1px, -1px)', boxShadow: HARD_SHADOW.button }}
          _open={{ bg: 'card', boxShadow: HARD_SHADOW.button }}
          css={{ '&[data-state=open] [data-caret]': { transform: 'rotate(180deg)' } }}
        >
          <GlobeSimpleIcon size={16} weight="bold" aria-hidden />
          {current?.label}
          <Box as="span" data-caret display="inline-flex" transition="transform 0.2s">
            <CaretDownIcon size={12} weight="bold" aria-hidden />
          </Box>
        </Box>
      </MenuTrigger>
      <Portal disabled={inDialog}>
        <MenuPositioner>
          <MenuContent
            minW="200px"
            p="6px"
            bg="card"
            border="2px solid"
            borderColor="ink"
            borderRadius="16px"
            boxShadow={HARD_SHADOW.button}
          >
            <Eyebrow fontSize="11px" px="12px" pt="6px" pb="8px" mb="4px" borderBottom={INK_RULE.dashed}>
              {copy.nav.language}
            </Eyebrow>
            {languages.map((item) => {
              const isCurrent = item.locale === locale;
              return (
                <MenuItem key={item.locale} value={item.locale} asChild borderRadius="10px" cursor="pointer" p="0" _highlighted={{ bg: 'sand' }}>
                  <RouterLink
                    to={item.to}
                    lang={item.lang}
                    hrefLang={item.lang}
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={onNavigate}
                  >
                    <Flex
                      w="100%"
                      align="center"
                      justify="space-between"
                      gap="16px"
                      px="12px"
                      py="10px"
                      color={isCurrent ? 'stampDeep' : 'ink'}
                    >
                      <Text fontWeight={isCurrent ? '700' : '500'}>{item.name}</Text>
                      {isCurrent && <CheckIcon size={16} weight="bold" aria-hidden />}
                    </Flex>
                  </RouterLink>
                </MenuItem>
              );
            })}
          </MenuContent>
        </MenuPositioner>
      </Portal>
    </MenuRoot>
  );
};
